import { Modal } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getTenantId } from "../../../redux/features/auth/authSlice";
import { useGetAllTenantProcessQuery } from "../../../redux/services/processApiSlice";
import { useTenantSitesAndZones } from "../../../hooks/useTenantSitesAndZones";
import { UIAgGridTable } from "../../common/agGridTable";
import { UICalendar } from "../../common/uiCalendar";
import "./style.css";

type Filters = {
  userName: string;
  siteId?: string;
  zoneId?: string;
  tagId: string;
  productUPC: string;
  processId?: string;
  status: string;
  search: string;
  dateRange: moment.Moment[];
};

const initialFilters: Filters = {
  userName: "",
  siteId: undefined,
  zoneId: undefined,
  tagId: "",
  productUPC: "",
  processId: undefined,
  status: "",
  search: "",
  dateRange: [],
};

const statusOptions = [
  { label: "All", value: "" },
  { label: "Enabled", value: "enabled" },
  { label: "De-enabled", value: "de-enabled" },
];

import { AlertTriangle, BadgeCheck, ClipboardList, FileText, Package, ShieldOff, X } from "lucide-react";
import EnablementDetailDrawer, {
  TelemetryRecord,
} from "../enablement/EnablementDetailDrawer";
import { ReportToolbar, ReportFilterChips } from "../../common/reportToolbar";
import Select from "../../common/selectInput/Select";
import Input from "../../common/input/Input";
import { getEnablementColumns } from "./columns/enablementColumns";
import { MetricCard } from "./components/MetricCard";
import { MetricSkeleton } from "./components/MetricSkeleton";
import Button from "../../common/button/button";
import { Pagination } from "../../common/pagination";

type ReportPageProps = {
  useReportQuery: (params: any, options?: any) => any;
  useExportMutation: () => readonly [any, { isLoading: boolean }];
};

const EnablementReport = ({
  useReportQuery,
  useExportMutation,
}: ReportPageProps) => {
  const tenantId = useSelector(getTenantId);

  const {
    zoneRows,
    siteNameById,
    zoneNameById,
    siteOptionsById,
    zoneOptionsById,
  } = useTenantSitesAndZones(tenantId);

  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(initialFilters);

  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedEnablement, setSelectedEnablement] =
    useState<TelemetryRecord | null>(null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Toggle advanced filter panel
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const appliedSiteName = appliedFilters.siteId
    ? siteNameById.get(appliedFilters.siteId)
    : undefined;

  const appliedZoneName = appliedFilters.zoneId
    ? zoneNameById.get(appliedFilters.zoneId)
    : undefined;

  const applyFilters = () => {
    setAppliedFilters({
      ...filters,
      userName: filters.userName.trim(),
      tagId: filters.tagId.trim(),
      productUPC: filters.productUPC.trim(),
      search: filters.search.trim(),
    });

    setPage(1);
    setShowAdvancedFilters(false);
  };

  const applySearch = (searchValue: string) => {
    const targetSearch = searchValue.trim();

    setAppliedFilters((prev) => {
      if (prev.search === targetSearch) return prev;

      setPage(1);

      return {
        ...prev,
        search: targetSearch,
      };
    });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };

  const listFetchParams = useMemo(
    () => ({
      tenantId,
      page,
      limit: pageSize,
      search: appliedFilters.search || undefined,
      userName: appliedFilters.userName || undefined,
      siteId: appliedFilters.siteId,
      zoneId: appliedFilters.zoneId,
      tagId: appliedFilters.tagId || undefined,
      productUPC: appliedFilters.productUPC || undefined,
      processId: appliedFilters.processId,
      status: appliedFilters.status || undefined,
      from: appliedFilters.dateRange?.[0]
        ? moment.utc(appliedFilters.dateRange[0]).format("YYYY-MM-DD")
        : undefined,
      to: appliedFilters.dateRange?.[1]
        ? moment.utc(appliedFilters.dateRange[1]).format("YYYY-MM-DD")
        : undefined,
      siteName: appliedSiteName,
      zoneName: appliedZoneName,
      sortBy: sortField,
      sortOrder,
    }),
    [
      tenantId,
      page,
      pageSize,
      appliedFilters,
      appliedSiteName,
      appliedZoneName,
      sortField,
      sortOrder,
    ],
  );

  const [exportExcel, { isLoading: isExportLoading }] = useExportMutation();

  const EXPORT_LIMIT = 10000;

  // Actual execution logic for export
  const executeExport = async (limitToMax = false) => {
    try {
      const exportParams = {
        ...listFetchParams,
      };

      // Remove page setup to target full dataset
      delete exportParams.page;
      delete exportParams.limit;

      if (limitToMax) {
        exportParams.limit = EXPORT_LIMIT;
      }

      const file = await exportExcel(exportParams).unwrap();
      setIsExportModalOpen(false);
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  // Interceptor function for user click
  const handleExport = () => {
    if (totalCount > EXPORT_LIMIT) {
      setIsExportModalOpen(true);
    } else {
      executeExport(false);
    }
  };

  const { data: processData } = useGetAllTenantProcessQuery(
    { tenantId },
    { skip: !tenantId },
  );
  const processList = useMemo(() => {
    const rows = processData?.result?.rows ?? processData?.data ?? [];
    return Array.isArray(rows)
      ? rows.map((p: { id: string; name?: string }) => ({
          label: p.name ?? p.id,
          value: p.id,
        }))
      : [];
  }, [processData]);

  const processNameMap = useMemo(
    () =>
      Object.fromEntries(
        processList.map((p: { label: string; value: string }) => [
          p.value,
          p.label,
        ]),
      ),
    [processList],
  );

  const { data, isFetching, error, isError } = useReportQuery(listFetchParams, {
    skip: !tenantId,
    refetchOnMountOrArgChange: true,
  });

  console.log("re render enablement ");

  useEffect(() => {
    if (!isFetching && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isFetching]);

  const rawRows = useMemo(() => {
    if (isError) return [];

    const result = data?.result ?? data;
    const r = Array.isArray(result)
      ? result
      : (result?.rows ?? result?.data ?? []);

    return Array.isArray(r) ? r : [];
  }, [data, isError]);

  const backendCounts = useMemo(() => {
    if (isError) {
      return {
        total: 0,
        enabled: 0,
        deenabled: 0,
        totalProducts: 0,
      };
    }

    const result = data?.result ?? data;

    return {
      total: result?.count ?? result?.totalCount ?? result?.total ?? 0,
      enabled: result?.totalEnablements ?? 0,
      deenabled: result?.totalDeEnablements ?? 0,
      totalProducts: result?.totalProducts ?? 0,
    };
  }, [data, isError]);

  const totalCount = backendCounts.total || 0;

  const tableData = useMemo(() => {
    return rawRows.map((row: any) => {
      const resolvedProcessName =
        row.processName || processNameMap[row.processId] || row.processId;

      return {
        ...row,
        key: row.diId || row._id,
        serial: row.diId,
        facilityLocation: row.siteName,
        zone: row.zoneName,
        hardwareName: row.deviceName,
        processName: resolvedProcessName,
        process: resolvedProcessName,
        dateWritten: row.operationTime,
        success: ["enabled", "success", "successful"].includes(
          String(row.status).toLowerCase(),
        )
          ? "Enabled"
          : "De-enabled",
        markerMatrix:
          row.additionalData?.association?.map((a: any) =>
            String(a.type).toUpperCase(),
          ) || [],
      };
    });
  }, [rawRows, processNameMap]);

  const [knownUsers, setKnownUsers] = useState<Set<string>>(new Set());
  useEffect(() => {
    setKnownUsers((prev) => {
      const next = new Set(prev);
      let changed = false;
      rawRows.forEach((row: Record<string, unknown>) => {
        const name = String(row.userName ?? row.username ?? "").trim();
        if (name && !next.has(name)) {
          next.add(name);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [rawRows]);

  const userOptions = Array.from(knownUsers)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      label: name,
      value: name,
    }));

  const totalRecords = backendCounts.total;
  const successful = backendCounts.enabled;
  const failed = backendCounts.deenabled;
  const totalProducts = backendCounts.totalProducts;

  const ALL_COLUMNS = useMemo(() => getEnablementColumns(), []);

  // Column visibility for "Choose Columns"
  const [hiddenColumns, setHiddenColumns] = useState(
    new Set(
      ALL_COLUMNS.filter((col) => col.defaultHidden).map((col) => col.key),
    ),
  );

  const visibleColumns = useMemo(() => {
    return ALL_COLUMNS.filter((col) => !hiddenColumns.has(col.key));
  }, [ALL_COLUMNS, hiddenColumns]);

  const columns = visibleColumns;

  const handleGridSortChange = (sort: {
    field?: string;
    order?: "ascend" | "descend";
  }) => {
    setSortField(sort.field || "createdAt");
    setSortOrder(sort.order === "ascend" ? "ASC" : "DESC");
    setPage(1);
  };

  const activeFilterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];

    if (appliedFilters.userName) {
      chips.push({
        label: `User: ${appliedFilters.userName}`,
        onRemove: () => {
          setFilters((prev) => ({ ...prev, userName: "" }));
          setAppliedFilters((prev) => ({ ...prev, userName: "" }));
          refreshAfterChipRemove();
        },
      });
    }

    if (appliedFilters.siteId) {
      chips.push({
        label: `Site: ${
          siteNameById.get(appliedFilters.siteId) ?? appliedFilters.siteId
        }`,
        onRemove: () => {
          setFilters((prev) => ({ ...prev, siteId: undefined }));
          setAppliedFilters((prev) => ({ ...prev, siteId: undefined }));
          refreshAfterChipRemove();
        },
      });
    }

    if (appliedFilters.zoneId) {
      chips.push({
        label: `Zone: ${
          zoneNameById.get(appliedFilters.zoneId) ?? appliedFilters.zoneId
        }`,
        onRemove: () => {
          setFilters((prev) => ({ ...prev, zoneId: undefined }));
          setAppliedFilters((prev) => ({ ...prev, zoneId: undefined }));
          refreshAfterChipRemove();
        },
      });
    }

    if (appliedFilters.tagId) {
      chips.push({
        label: `Tag: ${appliedFilters.tagId}`,
        onRemove: () => {
          setFilters((prev) => ({ ...prev, tagId: "" }));
          setAppliedFilters((prev) => ({ ...prev, tagId: "" }));
          refreshAfterChipRemove();
        },
      });
    }

    if (appliedFilters.productUPC) {
      chips.push({
        label: `UPC: ${appliedFilters.productUPC}`,
        onRemove: () => {
          setFilters((prev) => ({ ...prev, productUPC: "" }));
          setAppliedFilters((prev) => ({ ...prev, productUPC: "" }));
          refreshAfterChipRemove();
        },
      });
    }

    if (appliedFilters.status) {
      chips.push({
        label: `Status: ${appliedFilters.status}`,
        onRemove: () => {
          setFilters((prev) => ({ ...prev, status: "" }));
          setAppliedFilters((prev) => ({ ...prev, status: "" }));
          refreshAfterChipRemove();
        },
      });
    }

    if (appliedFilters.dateRange?.length === 2) {
      chips.push({
        label: `Date: ${moment(appliedFilters.dateRange[0]).format(
          "DD/MM/YYYY",
        )} - ${moment(appliedFilters.dateRange[1]).format("DD/MM/YYYY")}`,
        onRemove: () => {
          setFilters((prev) => ({ ...prev, dateRange: [] }));
          setAppliedFilters((prev) => ({ ...prev, dateRange: [] }));
          refreshAfterChipRemove();
        },
      });
    }

    return chips;
  }, [appliedFilters, siteNameById, zoneNameById]);

  const refreshAfterChipRemove = () => {
    setPage(1);
  };

  const handleToggleColumn = useCallback((key: string) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return new Set(next); // force new reference
    });
  }, []);

  // Inside your component:
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        __skeleton: true as const, // 'as const' ensures the type is true, not boolean
        id: `skeleton-${i}`,
      })),
    [],
  );

  const handleRowClick = useCallback((row: TelemetryRecord) => {
    setSelectedEnablement(row);
  }, []);

  return (
    <div className="h-full flex flex-col min-h-0 rounded-md gap-0">
      <div className="enb-metrics-row">
        {isInitialLoad && isFetching ? (
          <>
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
          </>
        ) : (
          <>
            <MetricCard
              label="TOTAL RECORDS:"
              value={totalRecords}
              accent="default"
              icon={<ClipboardList />}
            />
            <MetricCard
              label="ENABLEMENTS"
              value={successful}
              accent="green"
              icon={<BadgeCheck />}
            />
            <MetricCard
              label="DE-ENABLEMENTS"
              value={failed}
              accent="red"
              icon={<ShieldOff />}
            />
            <MetricCard
              label="TOTAL PRODUCTS"
              value={totalProducts}
              accent="orange"
              icon={<Package />}
            />
          </>
        )}
      </div>
      <div className="mt-4">
        <ReportToolbar
          searchValue={filters.search}
          onSearchChange={(value) =>
            setFilters((prev) => ({
              ...prev,
              search: value,
            }))
          }
          onDebounceSearch={(value) => applySearch(value)}
          onSearchEnter={applySearch}
          searchPlaceholder="Search Tag ID, UPC"
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() =>
            setShowAdvancedFilters((show) => !show)
          }
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          activeFilterCount={activeFilterChips.length}
          onExportExcel={handleExport}
          onExportLoading={isExportLoading}
          chooseColumns={ALL_COLUMNS.map((col) => ({
            key: col.key,
            label: col.title,
          }))}
          hiddenColumns={hiddenColumns}
          onToggleColumn={handleToggleColumn}
        >
          <div className="">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {/* Username */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Username
                </label>
                <Select
                  placeholder="Username"
                  value={filters.userName}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      userName: e.target.value,
                    }))
                  }
                  options={userOptions}
                  allowClear
                  onClear={() =>
                    setFilters((prev) => ({
                      ...prev,
                      userName: "",
                    }))
                  }
                />
              </div>

              {/* Site */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Site
                </label>
                <Select
                  placeholder="Site"
                  value={filters.siteId}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      siteId: e.target.value,
                      zoneId: undefined,
                    }))
                  }
                  options={siteOptionsById}
                  allowClear
                  onClear={() =>
                    setFilters((prev) => ({
                      ...prev,
                      siteId: undefined,
                      zoneId: undefined,
                    }))
                  }
                />
              </div>

              {/* Zone */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Zone
                </label>
                <Select
                  placeholder="Zone"
                  value={filters.zoneId}
                  onChange={(e) => {
                    const value = e.target.value;

                    const zone = zoneRows.find(
                      (r) => String(r.id) === String(value),
                    );

                    setFilters((prev) => ({
                      ...prev,
                      zoneId: value,
                      siteId: zone?.siteId ? String(zone.siteId) : prev.siteId,
                    }));
                  }}
                  options={zoneOptionsById}
                  allowClear
                  onClear={() =>
                    setFilters((prev) => ({
                      ...prev,
                      zoneId: undefined,
                    }))
                  }
                />
              </div>

              {/* Tag ID */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Tag ID
                </label>
                <Input
                  placeholder="Tag ID"
                  value={filters.tagId}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      tagId: e.target.value,
                    }))
                  }
                  allowClear
                  onClear={() =>
                    setFilters((prev) => ({
                      ...prev,
                      tagId: "",
                    }))
                  }
                />
              </div>

              {/* UPC */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  UPC
                </label>
                <Input
                  placeholder="Product UPC"
                  value={filters.productUPC}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      productUPC: e.target.value,
                    }))
                  }
                  allowClear
                  onClear={() =>
                    setFilters((prev) => ({
                      ...prev,
                      productUPC: "",
                    }))
                  }
                />
              </div>

              {/* Process */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Process
                </label>
                <Select
                  placeholder="Process"
                  value={filters.processId}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      processId: e.target.value,
                    }))
                  }
                  options={processList}
                  allowClear
                  onClear={() =>
                    setFilters((prev) => ({
                      ...prev,
                      processId: undefined,
                    }))
                  }
                />
              </div>

              {/* Status */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Status
                </label>
                <Select
                  placeholder="Status"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  options={statusOptions}
                  allowClear
                  onClear={() =>
                    setFilters((prev) => ({
                      ...prev,
                      status: "",
                    }))
                  }
                />
              </div>

              {/* Time Range */}
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Time Range
                </label>
                <UICalendar
                  params={filters.dateRange}
                  setParams={(dates: any) =>
                    setFilters((prev) => ({
                      ...prev,
                      dateRange: dates || [],
                    }))
                  }
                  handleChange={(dates: moment.Moment[]) =>
                    setFilters((prev) => ({
                      ...prev,
                      dateRange: dates || [],
                    }))
                  }
                  className="w-full h-10"
                  hideArrows={true}
                />
              </div>
            </div>
          </div>
        </ReportToolbar>
      </div>

      <ReportFilterChips chips={activeFilterChips} onClearAll={resetFilters} />
      <div className="table-container">
        <UIAgGridTable
          columns={columns}
          data={isFetching ? skeletonRows : tableData}
          onSortChange={handleGridSortChange}
          onRowClick={handleRowClick}
        />

        <Pagination
          current={page}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPage(1);
            setPageSize(size);
          }}
        />
      </div>

      {/* Elegant Export Enforcement Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-amber-600 font-medium text-base">
            <AlertTriangle size={20} />
            <span>Export Limit Warning</span>
          </div>
        }
        open={isExportModalOpen}
        onCancel={() => setIsExportModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            variant="outline"
            className="mr-2"
            onClick={() => {
              setIsExportModalOpen(false);
              setShowAdvancedFilters(true); // Automatically guide them to filter pane
            }}
          >
            Refine Filters
          </Button>,
          <Button
            key="submit"
            disabled={isExportLoading}
            onClick={() => executeExport(true)}
          >
            {isExportLoading
              ? "Exporting..."
              : `Download First ${EXPORT_LIMIT.toLocaleString()} Rows`}
          </Button>,
        ]}
      >
        <div className="py-2 text-sm text-gray-600 leading-relaxed">
          Your current filter selection contains{" "}
          <span className="font-semibold text-gray-900">
            {totalCount.toLocaleString()}
          </span>{" "}
          records. To ensure smooth browser performance, Excel generation is
          limited to a maximum of{" "}
          <span className="font-semibold text-gray-900">
            {EXPORT_LIMIT.toLocaleString()}
          </span>{" "}
          rows per download.
          <br />
          <br />
          Would you like to export the first {EXPORT_LIMIT.toLocaleString()}{" "}
          records matching your current sort order, or close this window to
          narrow your date range or site configurations?
        </div>
      </Modal>
      <EnablementDetailDrawer
        selectedEnablement={selectedEnablement}
        setSelectedEnablement={setSelectedEnablement}
      />
    </div>
  );
};

export default EnablementReport;
